import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import './ResmiTatiller.css';

// "Resmi ve Dini Tatiller" ekranı — CALLCENTER.RESMITATIL tablosu (kullanıcı talebi: 2026-09-05).
//
// Bu liste sadece bilgi amaçlı değildir: Call Center'da TEKRAR ARAMA tarihi buradaki günlere
// denk gelemez. Yani buraya eklenen her gün, o gün için randevu/arama planlanmasını ENGELLER —
// ekranda da bu açıkça yazıyor ki yanlışlıkla gün eklenmesin.
//
// Yetki: TEKN.YETK21 — Dr.Plazma'nın klasik yetki kolonu, VB6 modülleriyle ORTAK
// (menü ve route src/yetki.js + App.js'te bu numaraya bağlı).

const AY = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
            'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const GUN = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

// "2026-08-30" -> "30 Ağustos 2026 · Pazar". Tarih string'i sunucudan gün bazında geliyor;
// new Date('yyyy-MM-dd') UTC yorumlandığı için parçalayarak yerel tarih kuruluyor.
function tarihYaz(iso) {
  const [y, a, g] = iso.split('-').map(Number);
  const d = new Date(y, a - 1, g);
  return `${g} ${AY[a - 1]} ${y} · ${GUN[d.getDay()]}`;
}

const bosForm = { id: null, tarih: '', adi: '' };

// Çakışma listesi — hem randevular hem tekrar aramalar için aynı görünüm.
// Birden fazla kayıt olabildiği için her zaman LİSTE halinde gösteriliyor; sunucu en fazla
// `sinir` satır döndürür, gerisi "ve N tane daha" olarak yazılır.
function Cakismalar({ baslik, toplam, kayitlar, sinir, ipucu }) {
  if (!toplam) return null;
  const kalan = toplam - kayitlar.length;
  return (
    <div className="rt-cakisma">
      <strong>{baslik} ({toplam})</strong>
      <p className="rt-cakisma-ipucu">{ipucu}</p>
      <ul>
        {kayitlar.map((k, i) => (
          <li key={i}>
            {k.saat && <span className="rt-saat">{k.saat}</span>}
            <strong>{k.adSoy}</strong>
            {k.sube && <span className="rt-rozet">{k.sube}</span>}
            {k.ayrinti && <span className="rt-ayrinti"> {k.ayrinti}</span>}
          </li>
        ))}
      </ul>
      {kalan > 0 && <p className="rt-cakisma-ipucu">…ve {kalan} kayıt daha (ilk {sinir} tanesi listelendi).</p>}
    </div>
  );
}

function ResmiTatiller() {
  const [kayitlar, setKayitlar] = useState([]);
  const [yillar, setYillar] = useState([]);
  const [yil, setYil] = useState('');
  const [yukleniyor, setYukleniyor] = useState(true);
  const [form, setForm] = useState(bosForm);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [silinecek, setSilinecek] = useState(null);
  // Girilen tarihte randevu / tekrar arama var mı? Tarih değiştikçe sunucuya sorulur.
  const [kontrol, setKontrol] = useState(null);
  const [kontrolEdiliyor, setKontrolEdiliyor] = useState(false);
  // Uyarı gösterildikten sonra kullanıcı yine de devam etmek isterse true olur.
  const [uyariGecildi, setUyariGecildi] = useState(false);
  const [hata, setHata] = useState('');
  const [bilgi, setBilgi] = useState('');

  const listele = useCallback(async () => {
    setYukleniyor(true);
    setHata('');
    try {
      const { data } = await api.get('/ResmiTatil', { params: yil ? { yil } : {} });
      setKayitlar(data.kayitlar);
      setYillar(data.yillar);
    } catch (err) {
      setHata(err.response?.data?.hata || 'Tatil listesi alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, [yil]);

  useEffect(() => { listele(); }, [listele]);

  // Tarih her değiştiğinde çakışma kontrolü. Kullanıcı "Ekle"ye basmadan ÖNCE görsün diye
  // burada yapılıyor; kaydetme anında da tekrar bakılıyor (aradan zaman geçmiş olabilir).
  useEffect(() => {
    setUyariGecildi(false);
    if (!form.tarih) { setKontrol(null); return; }

    let iptal = false;
    setKontrolEdiliyor(true);
    api.get('/ResmiTatil/kontrol', { params: { tarih: form.tarih } })
      .then(({ data }) => { if (!iptal) setKontrol(data); })
      .catch(() => { if (!iptal) setKontrol(null); })
      .finally(() => { if (!iptal) setKontrolEdiliyor(false); });

    // Hızlı tarih değişiminde eski yanıtın yenisini ezmesini engeller.
    return () => { iptal = true; };
  }, [form.tarih]);

  const kaydet = async (e) => {
    e.preventDefault();
    setHata(''); setBilgi('');

    // Çakışma varsa ilk basışta kaydetme: uyarıyı göster, onay bekle.
    if (kontrol?.cakisma && !uyariGecildi) {
      setUyariGecildi(true);
      return;
    }

    setKaydediliyor(true);
    try {
      if (form.id) {
        await api.put(`/ResmiTatil/${form.id}`, { tarih: form.tarih, adi: form.adi });
        setBilgi(`${form.adi} güncellendi.`);
      } else {
        await api.post('/ResmiTatil', { tarih: form.tarih, adi: form.adi });
        setBilgi(`${form.adi} eklendi. Bu tarihe artık tekrar arama verilemez.`);
      }
      setForm(bosForm);
      setKontrol(null);
      setUyariGecildi(false);
      listele();
    } catch (err) {
      setHata(err.response?.data?.hata || 'Kayıt yapılamadı.');
    } finally {
      setKaydediliyor(false);
    }
  };

  const sil = async () => {
    setHata(''); setBilgi('');
    try {
      await api.delete(`/ResmiTatil/${silinecek.id}`);
      setBilgi(`${silinecek.adi} listeden çıkarıldı.`);
      if (form.id === silinecek.id) setForm(bosForm);
      setSilinecek(null);
      listele();
    } catch (err) {
      setHata(err.response?.data?.hata || 'Kayıt silinemedi.');
      setSilinecek(null);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ marginBottom: 4, color: 'var(--med-dark)' }}>📅 Resmi ve Dini Tatiller</h2>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          Buradaki günler <strong>tüm şubeler için ortaktır</strong>. Call Center'da
          <strong> tekrar arama tarihi</strong> bu günlere verilemez.
        </p>
      </div>

      {bilgi && <div className="basari-kutu">{bilgi}</div>}
      {hata && <div className="hata-kutu">{hata}</div>}

      <div className="rt-duzen">
        {/* Sol kolon: ekleme / düzenleme formu. Aynı form iki iş için kullanılıyor;
            düzenleme moduna satırdaki kalem düğmesiyle geçiliyor. */}
        <form className="card rt-form" onSubmit={kaydet}>
          <strong style={{ color: 'var(--med-dark)', fontSize: 14 }}>
            {form.id ? '✏️ Kaydı Düzenle' : '➕ Yeni Tatil Günü'}
          </strong>

          <div style={{ marginTop: 12 }}>
            <label className="rt-etiket">TARİH</label>
            <input type="date" value={form.tarih} required
                   onChange={e => setForm({ ...form, tarih: e.target.value })} />
          </div>

          {kontrolEdiliyor && <p className="rt-kontrol">Bu tarih kontrol ediliyor…</p>}

          {kontrol && !kontrol.cakisma && form.tarih && (
            <p className="rt-temiz">✔ Bu tarihte randevu ve tekrar arama yok.</p>
          )}

          {kontrol?.cakisma && (
            <div className="rt-uyari">
              <Cakismalar
                baslik="⚠ Bu tarihte RANDEVU var"
                toplam={kontrol.randevuToplam}
                kayitlar={kontrol.randevular}
                sinir={kontrol.sinir}
                ipucu="Önce bu randevuları başka güne kaydırın."
              />
              <Cakismalar
                baslik="⚠ Bu tarihe TEKRAR ARAMA verilmiş"
                toplam={kontrol.aramaToplam}
                kayitlar={kontrol.aramalar}
                sinir={kontrol.sinir}
                ipucu="Bu kişilerin arama tarihini değiştirin."
              />
              {uyariGecildi && (
                <p className="rt-uyari-onay">
                  Yine de eklemek istiyorsanız düğmeye bir daha basın — çakışan kayıtlar
                  KENDİLİĞİNDEN taşınmaz, tek tek düzeltilmelidir.
                </p>
              )}
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <label className="rt-etiket">TATİLİN ADI</label>
            <input type="text" value={form.adi} required maxLength={255}
                   placeholder="ör. 29 Ekim Cumhuriyet Bayramı"
                   onChange={e => setForm({ ...form, adi: e.target.value })} />
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={kaydediliyor}>
              {kaydediliyor ? '...'
                : kontrol?.cakisma && uyariGecildi ? '⚠ Yine de kaydet'
                : (form.id ? '💾 Güncelle' : '➕ Ekle')}
            </button>
            {form.id && (
              <button type="button" className="btn btn-secondary" onClick={() => setForm(bosForm)}>
                Vazgeç
              </button>
            )}
          </div>

          <p className="rt-not">
            Yarım günlük tatiller (arife) burada YOKTUR; eklenirse o gün tamamen kapalı sayılır.
          </p>
        </form>

        {/* Sağ kolon: liste. */}
        <div className="rt-sag">
          <div className="rt-serit">
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {kayitlar.length} tatil günü.
            </span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              Yıl:
              <select value={yil} onChange={e => setYil(e.target.value)} style={{ width: 'auto' }}>
                <option value="">Tümü</option>
                {yillar.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          </div>

          {yukleniyor && <div className="loading">Yükleniyor...</div>}

          {!yukleniyor && kayitlar.length === 0 && (
            <div className="empty">
              {yil ? `${yil} yılı için tatil günü tanımlı değil.` : 'Henüz tatil günü tanımlanmamış.'}
            </div>
          )}

          {!yukleniyor && kayitlar.length > 0 && (
            <div className="card" style={{ padding: 0 }}>
              <table className="rt-tablo">
                <colgroup>
                  <col style={{ width: '34%' }} />
                  <col style={{ width: '50%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <thead>
                  <tr><th>TARİH</th><th>TATİLİN ADI</th><th style={{ textAlign: 'right' }}>İŞLEM</th></tr>
                </thead>
                <tbody>
                  {kayitlar.map(k => (
                    /* Geçmiş günler soluk: liste yıllar biriktikçe uzayacak, hangisinin
                       hâlâ etkili olduğu bir bakışta görünsün. */
                    <tr key={k.id} className={k.gecmis ? 'rt-gecmis' : ''}>
                      <td>{tarihYaz(k.tarih)}</td>
                      <td title={k.adi}>{k.adi}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="rt-ikon rt-ikon-duzenle" title="Düzenle"
                                onClick={() => { setBilgi(''); setForm({ id: k.id, tarih: k.tarih, adi: k.adi || '' }); }}>
                          ✏️
                        </button>
                        <button className="rt-ikon rt-ikon-sil" title="Sil"
                                onClick={() => setSilinecek(k)}>
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Silme onayı — kayıt geri alınamaz şekilde siliniyor (tabloda IPTAL kolonu yok). */}
      {silinecek && (
        <div className="rt-modal-zemin" onClick={() => setSilinecek(null)}>
          <div className="rt-modal" onClick={e => e.stopPropagation()}>
            <strong style={{ color: 'var(--med-dark)' }}>Tatil günü silinsin mi?</strong>
            <p style={{ margin: '10px 0', fontSize: 13.5 }}>
              <strong>{tarihYaz(silinecek.tarih)}</strong><br />
              {silinecek.adi}
            </p>
            <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Silinirse bu güne tekrar arama tarihi verilebilir hale gelir. Kayıt geri alınamaz.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSilinecek(null)}>Vazgeç</button>
              <button className="btn btn-danger" onClick={sil}>Sil</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResmiTatiller;
